/**
 * Plinko — public-survey adapter. The game CODE stays `plinko` (bs_event.gameId
 * FK + registry key), but the visual is the design-authored "Xinkit"
 * drop-and-bounce canvas (`_vendor/XinkitCanvas`, ported from the root
 * `/games` library).
 *
 * Picks the winner once via the shared `pickPrize`, resolves the brand, and
 * bridges the canvas `onRevealed(prize, bucketIndex)` → `onWin`. Visuals and
 * the honest-scatter physics live in the canvas.
 */
import { useMemo } from 'react';
import type { GamePlayProps } from './pickPrize';
import { pickPrize } from './pickPrize';
import { Xinkit as XinkitCanvas, type BallVariant } from './_vendor/XinkitCanvas';
import { resolveBrand } from './_shared/resolveBrand';
import { gameLang, toSegment } from './_shared/segments';
import { GameEmpty } from './_shared/GameEmpty';

export function Plinko({ items, primary, accent, onWin, lang, logo, config }: GamePlayProps) {
  const valid = useMemo(() => items.filter((i) => i.available > 0), [items]);
  const winnerIdx = useMemo(() => pickPrize(valid), [valid]);

  if (valid.length === 0 || winnerIdx < 0) return <GameEmpty />;

  const winner = valid[winnerIdx];
  const brand = resolveBrand({ primary, accent, logo });

  return (
    <div className="flex w-full justify-center">
      <XinkitCanvas
        prize={toSegment(winner)}
        brand={brand}
        ballVariant={(config?.ballVariant as BallVariant | undefined) ?? 'logo'}
        wordmark={typeof config?.wordmark === 'string' ? config.wordmark : undefined}
        tagline={typeof config?.tagline === 'string' ? config.tagline : undefined}
        language={gameLang(lang)}
        onRevealed={() => onWin(winner)}
      />
    </div>
  );
}
